import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

export type ClientProgramStatus = 'Planned' | 'Active' | 'On Hold' | 'Discontinued' | 'Mastered';

export interface ClientProgram {
    id: string;
    clientId: string;
    programDomainId: string | null;
    programDomain: string;
    sourceBankId: string | null;
    name: string;
    teachingMethod: string;
    description: string;
    status: ClientProgramStatus;
}

export interface ClientProgramWritePayload {
    clientId?: string;
    name?: string;
    programDomainId?: string | null;
    teachingMethod?: string;
    description?: string;
    status?: ClientProgramStatus;
}

interface ListOpts {
    clientId: string;
    status?: ClientProgramStatus;
    includeRetired?: boolean;
}

async function fetchClientPrograms(opts: ListOpts): Promise<ClientProgram[]> {
    const params: Record<string, string> = { clientId: opts.clientId };
    if (opts.status) params.status = opts.status;
    if (opts.includeRetired) params.includeRetired = '1';
    const res = await api.get<ClientProgram[]>('/client-programs', { params });
    return res.data;
}

export function useClientPrograms(opts: ListOpts) {
    return useQuery({
        queryKey: [
            'client-programs',
            opts.clientId,
            opts.status ?? 'any',
            opts.includeRetired ? 'with-retired' : 'active',
        ] as const,
        queryFn: () => fetchClientPrograms(opts),
        enabled: !!opts.clientId,
        staleTime: 60_000,
    });
}

async function fetchClientProgram(id: string): Promise<ClientProgram> {
    const res = await api.get<ClientProgram>(`/client-programs/${id}`);
    return res.data;
}

export function useClientProgram(id: string | undefined) {
    return useQuery({
        queryKey: ['client-programs', 'entry', id] as const,
        queryFn: () => fetchClientProgram(id!),
        enabled: !!id,
    });
}

export function useCreateClientProgram(clientId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (payload: ClientProgramWritePayload) => {
            const res = await api.post<ClientProgram>('/client-programs', { ...payload, clientId });
            return res.data;
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['client-programs', clientId] }),
    });
}

export function useUpdateClientProgram(clientId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: string; payload: ClientProgramWritePayload }) => {
            const res = await api.patch<ClientProgram>(`/client-programs/${id}`, payload);
            return res.data;
        },
        onSuccess: (_data, vars) => {
            qc.invalidateQueries({ queryKey: ['client-programs', clientId] });
            qc.invalidateQueries({ queryKey: ['client-programs', 'entry', vars.id] });
            qc.invalidateQueries({ queryKey: ['client-program-targets'] });
        },
    });
}

export function useRetireClientProgram(clientId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            await api.delete(`/client-programs/${id}`);
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['client-programs', clientId] });
            qc.invalidateQueries({ queryKey: ['client-program-targets'] });
        },
    });
}

export interface SaveProgramToBankResponse {
    bankId: string;
}

export function useSaveClientProgramToBank(clientId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, createNew }: { id: string; createNew?: boolean }) => {
            const res = await api.post<SaveProgramToBankResponse>(
                `/client-programs/${id}/save-to-bank`,
                createNew ? { createNew: true } : {},
            );
            return res.data;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['client-programs', clientId] });
            qc.invalidateQueries({ queryKey: ['program-bank'] });
        },
    });
}
